/**
 * Keylogx HGS Aktarım eklentisi (Chrome/Edge) ile sayfa arasındaki köprü (04.11.2026).
 * Sayfa → eklenti: window.postMessage({ kaynak: 'keylogx-web', id, tip, veri }, location.origin)
 * Eklenti → sayfa: { kaynak: 'keylogx-hgs-eklenti', id, cevap }
 * Eklenti kuruluysa <html data-keylogx-hgs-eklenti="sürüm"> taşır. HGS şifresi / çerezi hiçbir zaman bu köprüden geçmez.
 */
export const HGS_ADRESI = 'https://hgsmusteri.ptt.gov.tr/hgs.jsf';

export type EklentiTipi = 'hgsEklentiDurum' | 'hgsEklentiBagla' | 'hgsCek' | 'hgsBaglantiKes';

export interface EklentiCevabi {
    ok: boolean
    kod?: string | null
    hata?: string
    [alan: string]: any
}

/** Test edilebilirlik için pencerenin yalnız kullanılan parçası. */
export interface KopruPenceresi {
    location: { origin: string }
    postMessage(mesaj: any, hedef: string): void
    addEventListener(tur: 'message', f: (e: MessageEvent) => void): void
    removeEventListener(tur: 'message', f: (e: MessageEvent) => void): void
}

export const eklentiSurumu = (belge: Document = document): string | null => belge.documentElement.dataset.keylogxHgsEklenti || null;

let sayac = 0;

export function eklentiyeSor(tip: EklentiTipi, veri?: any, zamanAsimiMs = 5000, pencere: KopruPenceresi = window, kurulu: () => boolean = () => !!eklentiSurumu()): Promise<EklentiCevabi> {
    const id = `k${Date.now()}-${++sayac}`;
    return new Promise(coz => {
        let zamanlayici: any;
        const dinle = (e: MessageEvent) => {
            if (e.source !== (pencere as any) || e.origin !== pencere.location.origin) return;
            const m = e.data;
            if (!m || m.kaynak !== 'keylogx-hgs-eklenti' || m.id !== id) return;
            pencere.removeEventListener('message', dinle);
            clearTimeout(zamanlayici);
            coz(m.cevap ?? { ok: false, hata: 'Eklentiden boş yanıt geldi.' });
        };
        pencere.addEventListener('message', dinle);
        zamanlayici = setTimeout(() => {
            pencere.removeEventListener('message', dinle);
            coz({ ok: false, kod: kurulu() ? 'ZAMAN_ASIMI' : 'EKLENTI_YOK',
                hata: kurulu() ? 'Eklenti yanıt vermedi. Sayfayı yenileyip yeniden deneyin.' : 'Keylogx HGS Aktarım eklentisi bu tarayıcıda kurulu değil.' });
        }, zamanAsimiMs);
        pencere.postMessage({ kaynak: 'keylogx-web', id, tip, veri: veri ?? null }, pencere.location.origin);
    });
}

const OTURUM_ADLARI: Record<string, string> = {
    GIRIS_YAPILDI: 'Giriş yapıldı',
    GIRIS_YAPILMADI: 'Giriş yapılmadı',
    OTURUM_DUSTU: 'Oturum sona erdi',
    SEKME_YOK: 'HGS sekmesi açık değil',
    BILINMIYOR: 'Bilinmiyor',
};
export const oturumDurumuAdi = (d?: string | null) => (d && OTURUM_ADLARI[d]) || 'Bilinmiyor';
export const oturumRengi = (d?: string | null) => d === 'GIRIS_YAPILDI' ? 'success' : d === 'OTURUM_DUSTU' ? 'danger' : d === 'GIRIS_YAPILMADI' ? 'warning' : 'secondary';

export const saatGecerli = (s: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(s.trim());
export const hesapNoTemizle = (s: string) => s.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
export const hesapNoGecerli = (s: string) => { const t = hesapNoTemizle(s); return t.length >= 4 && t.length <= 30; };

/** Aktarım durumu rengi: 1 Başarılı, 2 Kısmen, 3 Hatalı. */
export const aktarimRengi = (durum: number) => durum === 1 ? 'success' : durum === 2 ? 'warning' : 'danger';

export const tarihSaat = (iso?: string | null) => {
    if (!iso) return '';
    const t = new Date(iso);
    return isNaN(t.getTime()) ? '' : t.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
export const tarih = (iso?: string | null) => {
    if (!iso) return '';
    const t = new Date(iso);
    return isNaN(t.getTime()) ? '' : t.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
export const para = (n?: number | null) => n == null ? '' : n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
